import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Save, RotateCcw, History, Lock, Sliders } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', saffron: '#FF9933' };

const CLASS_LABELS = {
  sme_commercial: 'SME Commercial', public_liability: 'Public Liability',
  commercial_motor: 'Commercial Motor', professional_indemnity: 'Professional Indemnity', cyber: 'Cyber',
};
const SKIP = new Set(['class', 'version']);
const LOCKED_VALUES = new Set(['REFER', 'MANDATORY', 'MANUAL']);

const pretty = (k) => String(k).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmt$ = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(1)}K` : `$${Number(v || 0).toLocaleString()}`;

function setPath(obj, path, value) {
  const next = structuredClone(obj);
  let cur = next;
  for (let i = 0; i < path.length - 1; i++) cur = cur[path[i]];
  cur[path[path.length - 1]] = value;
  return next;
}

function NumberInput({ value, onChange }) {
  const step = Math.abs(value) < 1 ? 0.0001 : Math.abs(value) < 10 ? 0.01 : 1;
  return (
    <input type="number" step={step} value={value}
      onChange={(e) => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))}
      className="w-28 px-2 py-1 text-xs font-mono rounded border text-right"
      style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
  );
}

export default function RateEditorPage() {
  const api = useApi();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('sme_commercial');
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [label, setLabel] = useState('');
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const loadCfg = useCallback(() => {
    Promise.all([api.get('/rates/tables'), api.get('/rates/versions')])
      .then(([t, v]) => {
        setCfg(t.data); setVersions(v.data.versions || []);
        setDraft(structuredClone(t.data.tables[selected]));
      })
      .catch(e => console.error(e?.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => { loadCfg(); }, [loadCfg]);

  // when class changes, reset draft from cfg
  useEffect(() => {
    if (cfg) { setDraft(structuredClone(cfg.tables[selected])); setLabel(''); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // debounced live preview
  useEffect(() => {
    if (!draft || !cfg) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      api.post('/rates/preview', { class_id: selected, table: draft, sample: cfg.samples?.[selected] })
        .then(r => setPreview(r.data.quote)).catch(() => {});
    }, 450);
    return () => clearTimeout(debounceRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, selected]);

  const dirty = cfg && draft && JSON.stringify(draft) !== JSON.stringify(cfg.tables[selected]);

  const save = () => {
    setSaving(true);
    api.put(`/rates/tables/${selected}`, { table: draft, label: label || `Updated ${CLASS_LABELS[selected]}` })
      .then((r) => { toast.success(`Submitted for actuarial sign-off — ${r.data.change_count} change(s) pending review`); setLabel(''); loadCfg(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Submit failed'))
      .finally(() => setSaving(false));
  };

  const rollback = (v) => {
    api.post(`/rates/rollback/${v}`)
      .then(() => { toast.success(`Rolled back to version ${v}`); loadCfg(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Rollback failed'));
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  if (!cfg || !draft) return <div className="text-sm" style={{ color: C.slate }}>Unable to load rate tables.</div>;

  return (
    <div className="space-y-4" data-testid="rate-editor-page">
      <div className="px-4 py-3 rounded-md text-[11px] leading-relaxed" data-testid="reference-implementation-banner"
        style={{ background: 'rgba(26,76,124,0.05)', border: '1px solid rgba(26,76,124,0.15)', color: C.slate }}>
        <span className="font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Reference implementation</span> — the rates,
        appetite rules and worked examples here demonstrate that the platform carries each class end to end. They are the shape a
        tenant's numbers slot into, not recommendations. An agency loads its own rates and appetite.
      </div>
      {/* Class switcher + version badge */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5" data-testid="rate-class-switcher">
          {Object.keys(cfg.tables).map(cid => (
            <button key={cid} onClick={() => setSelected(cid)} data-testid={`rate-class-${cid}`}
              className="px-3 py-2 rounded-md text-xs font-semibold transition-all"
              style={selected === cid ? { background: C.navy, color: 'white' } : { background: 'white', color: C.slate, border: '1px solid rgba(136,150,168,0.2)' }}>
              {CLASS_LABELS[cid] || cid}
            </button>
          ))}
        </div>
        <span className="text-xs" style={{ color: C.slate }}>Active <span className="font-mono font-semibold" style={{ color: C.blue }}>v{cfg.version}</span> · {cfg.updated_by}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Editor */}
        <div className="lg:col-span-2 space-y-3" data-testid="rate-editor-fields">
          {Object.entries(draft).filter(([k]) => !SKIP.has(k)).map(([key, val]) => {
            if (typeof val === 'number') {
              return (
                <Card key={key} className="px-4 py-3 rounded-md flex items-center justify-between" style={{ border: '1px solid rgba(136,150,168,0.14)' }}>
                  <span className="text-sm font-medium" style={{ color: C.navy }}>{pretty(key)}</span>
                  <NumberInput value={val} onChange={(v) => setDraft(setPath(draft, [key], v))} />
                </Card>
              );
            }
            if (val && typeof val === 'object' && !Array.isArray(val)) {
              return (
                <Card key={key} className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Sliders className="w-3.5 h-3.5" style={{ color: C.blue }} />
                    <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>{pretty(key)}</h3>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {Object.entries(val).map(([sk, sv]) => (
                      <div key={sk} className="flex items-center justify-between gap-2">
                        <span className="text-xs truncate" style={{ color: C.slate }}>{pretty(sk)}</span>
                        {typeof sv === 'number'
                          ? <NumberInput value={sv} onChange={(v) => setDraft(setPath(draft, [key, sk], v))} />
                          : <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded shrink-0" style={{ background: LOCKED_VALUES.has(sv) ? '#FBE9E7' : '#EEF1F5', color: LOCKED_VALUES.has(sv) ? C.red : C.slate }} title="Rule-controlled, not numerically editable"><Lock className="w-2.5 h-2.5" />{sv}</span>}
                      </div>
                    ))}
                  </div>
                </Card>
              );
            }
            return null;
          })}
        </div>

        {/* Live preview + actions */}
        <div className="space-y-3">
          <Card className="p-4 rounded-md sticky top-2" style={{ border: `1px solid ${C.teal}40`, background: '#F7FCFE' }} data-testid="rate-preview">
            <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Live Premium Preview</h3>
            {preview ? (
              <>
                <p className="text-3xl font-bold font-mono" style={{ color: C.navy }} data-testid="rate-preview-gross">{fmt$(preview.gross_premium)}</p>
                <p className="text-[11px] mb-3" style={{ color: C.slate }}>gross premium · sample risk</p>
                <div className="space-y-1 text-xs">
                  <Row label="Net premium" value={fmt$(preview.net_premium)} />
                  <Row label="Stamp duty" value={fmt$(preview.stamp_duty)} />
                  <Row label="GST" value={fmt$(preview.gst)} />
                  <div className="border-t my-1.5" style={{ borderColor: 'rgba(136,150,168,0.15)' }} />
                  <Row label="Total payable" value={fmt$(preview.total_payable)} bold />
                </div>
                {preview.decision === 'HARD_DECLINE' && <p className="mt-2 text-xs font-semibold" style={{ color: C.red }}>HARD DECLINE: {preview.reason}</p>}
              </>
            ) : <Skeleton className="h-20" />}
            {dirty && <p className="mt-3 text-[11px] font-medium" style={{ color: C.amber }}>Unsaved changes — preview reflects your draft.</p>}
          </Card>

          <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }}>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Change note (optional)"
              data-testid="rate-save-label"
              className="w-full px-2.5 py-2 text-xs rounded border mb-2" style={{ borderColor: 'rgba(136,150,168,0.3)' }} />
            <div className="flex gap-2">
              <button onClick={save} disabled={!dirty || saving} data-testid="rate-save-btn"
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all"
                style={{ background: dirty && !saving ? C.green : '#D5DBE2', color: 'white', cursor: dirty && !saving ? 'pointer' : 'not-allowed' }}>
                <Save className="w-3.5 h-3.5" />{saving ? 'Submitting…' : 'Submit for Sign-Off'}
              </button>
              <button onClick={() => setDraft(structuredClone(cfg.tables[selected]))} disabled={!dirty} data-testid="rate-reset-btn"
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold"
                style={{ background: 'white', color: C.slate, border: '1px solid rgba(136,150,168,0.25)' }}>
                <RotateCcw className="w-3.5 h-3.5" />Reset
              </button>
            </div>
          </Card>

          <Card className="rounded-md overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid="rate-versions">
            <div className="p-3 flex items-center gap-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.1)' }}>
              <History className="w-3.5 h-3.5" style={{ color: C.slate }} />
              <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Version History</h3>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {versions.map(v => (
                <div key={v.version} className="flex items-center justify-between gap-2 px-3 py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                  <div className="min-w-0">
                    <span className="text-xs font-mono font-semibold mr-1.5" style={{ color: v.active ? C.green : C.slate }}>v{v.version}{v.active ? ' ●' : ''}</span>
                    <span className="text-xs" style={{ color: C.navy }}>{v.label}</span>
                  </div>
                  {!v.active && (
                    <button onClick={() => rollback(v.version)} data-testid={`rate-rollback-${v.version}`}
                      className="text-[10px] font-semibold px-2 py-1 rounded shrink-0" style={{ background: '#EEF1F5', color: C.blue }}>Restore</button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: C.slate }}>{label}</span>
      <span className="font-mono" style={{ color: C.navy, fontWeight: bold ? 700 : 400 }}>{value}</span>
    </div>
  );
}
